'use client';

import { Button } from '@common/components';
import { useToast } from '@common/hooks';
import { SvgTarget2 } from '@common/icon';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { usePathname } from 'next/navigation';
import { useShallow } from 'zustand/react/shallow';

import { AuthMutation, MEMBER_JOIN_STATUS } from '@/api';
import { ResponsiveDialog } from '@/components/organism/ResponsiveDialog';
import { ROUTES } from '@/constants';
import { DEFAULT_LOCATION, useLocation } from '@/hooks/useLocation';
import { useLocationConsent } from '@/hooks/useLocationConsent';
import { useAuthStore } from '@/stores/AuthStore';

export const RegionDrawer = () => {
  const [isLogin, myInfo] = useAuthStore(
    useShallow((state) => [state.isLogin, state.myInfo]),
  );
  const queryClient = useQueryClient();

  const { isConsented } = useLocationConsent();

  const pathname = usePathname();

  const needRegionData =
    myInfo?.memberJoinStatus === MEMBER_JOIN_STATUS.EMPTY_FILTER &&
    !(myInfo.codeRegion && myInfo.codeSubRegion);

  const location = useLocation();

  const { toastError } = useToast();

  const postRegionDataMutation = useMutation({
    ...AuthMutation['post:/wello/v2/auth/member-filter/region'](),
    onSuccess: () => {
      queryClient.invalidateQueries();
    },
    onError: (error) =>
      toastError({
        error,
      }),
  });

  if (!pathname || !isLogin || isConsented === undefined) return null;

  const enabledPaths = [
    ROUTES.HOME.pathname,
    ROUTES.MY.pathname,
    ROUTES.COMMUNITY.pathname,
    ROUTES.RECOMMEND_POLICY_LIST.pathname,
  ];
  const excludedPaths = [ROUTES.POINT_MARKET_HISTORY.pathname];

  const isEnabledPathname =
    enabledPaths.some((path) => pathname.startsWith(path)) &&
    !excludedPaths.some((path) => pathname.startsWith(path));

  return isEnabledPathname ? (
    <ResponsiveDialog
      opened={needRegionData}
      drawerType="bottom"
      className={{
        backdrop: 'z-50',
        drawer: 'z-50',
      }}
    >
      <h3 className="typo-body2-b mb-8 text-Gray-900">지역을 선택해 주세요</h3>
      <p className="typo-body4-r mb-16 text-Gray-700">
        선택된 지역의 정책과 게시글을 볼 수 있어요.
      </p>
      <ul className="pl-18 text-Gray-600 typo-caption1">
        <li className="list-disc">
          지역 설정은{' '}
          <span className="text-Gray-black">{`맞춤정책 > 맞춤정보 수정 > 지역`}</span>
          에서 언제든지 수정할 수 있어요.
        </li>
        <li className="list-disc">
          해외 접속 등 지역 정보 확인이 어려운 경우에는 &apos;서울시
          관악구&apos;로 기본 설정돼요.
        </li>
      </ul>
      <Button
        className="mt-20 w-full"
        loading={location.isLoading || postRegionDataMutation.isPending}
        onClick={async () => {
          const { data } = await location.refetch();

          const codeRegion = data?.codeRegion ?? DEFAULT_LOCATION.codeRegion;
          const codeSubRegion =
            data?.codeSubRegion ?? DEFAULT_LOCATION.codeSubRegion;

          postRegionDataMutation.mutate({
            body: {
              codeRegion: codeRegion,
              codeSubRegion: codeSubRegion,
            },
          });
        }}
      >
        <SvgTarget2 className="mr-6 size-16" name="현재 위치" />
        현재 위치로 선택하기
      </Button>
    </ResponsiveDialog>
  ) : null;
};
